<script>
import {use} from "echarts/core"
import {CanvasRenderer} from "echarts/renderers"
import {LineChart} from "echarts/charts"
import {
  LegendComponent,
  GridComponent,
  MarkLineComponent,
  TooltipComponent,
} from "echarts/components"
import VChart from "vue-echarts"
import {getStaffListAll} from "@/lib/lookup"
import {toLocalString} from "@/lib/dateTimeUtilities";


use([
  CanvasRenderer,
  LineChart,
  LegendComponent,
  GridComponent,
  MarkLineComponent,
  TooltipComponent,
])

export default {
  name: "StaffWorkload",
  components: {VChart},
  permissions: ["Read Timeseries"],
  computed: {
    staffOptions() {
      return this.staff.map(staffMember => ({
        text: staffMember.name,
        value: staffMember.bwf_userid,
      }))
    },
    workloadSummary() {
      // Summarize the workload for each staff member
      const summary = {}
      for (const staffMember of this.staff) {
        const entries = this.workload[staffMember.bwf_userid] || []
        const currentLoad = entries.reduce((sum, item) => sum + item.hours, 0)
        summary[staffMember.bwf_userid] = {
          name: staffMember.name,
          currentLoad: currentLoad,
          bwf_userid: staffMember.bwf_userid,
        }
      }
      return Object.values(summary)
    },
    workloadDetails() {
      // Detailed workload for the selected staff member
      if (!this.selectedStaff) return []
      return this.workload[this.selectedStaff].filter((item) => item.hours > 0).sort((a, b) => b.hours - a.hours)
    },
  },
  methods: {
    getJobName(jobId) {
      // Find the job name from the jobs list
      const job = this.jobs.find(item => item.ID === jobId)
      return job ? `${job.Client.Name} - ${job.Name}` : "Unknown Job"
    },
    loadJobs() {
      const endDate = new Date()
      const startDate = new Date()
      startDate.setFullYear(startDate.getFullYear() - this.$WfmApi.lookback)
      const start = toLocalString(startDate).substr(0, 10).replace(/-/g, "")
      const end = toLocalString(endDate).substr(0, 10).replace(/-/g, "")
      const query = {"from": start, "to": end, detailed: true}

      return this.$WfmApi.get("job.api/list", query).then(response => {
        this.jobs = this.$ensureArray(response.Jobs.Job)
      })
    },
    loadStaff() {
      getStaffListAll(this, (staff) => {
        this.staff = staff.filter(item => item.is_working)

        this.loaded = true
      })
    },
    async loadWorkload() {
      // Load the current workload information
      return this.$BwfApi.get("capacity-plan/staff").then(response => {
        this.workload = response
      })
    },
    async loadAll() {
      const timeSeriesLoaders = []
      timeSeriesLoaders.push(this.loadWorkload())
      timeSeriesLoaders.push(this.loadJobs())
      this.staff = await this.$BwfApi.get("staff")
      for (const staffMember of this.staff) {
        timeSeriesLoaders.push(this.loadTimeseries("Workload", staffMember.bwf_userid))
      }


      Promise.all(timeSeriesLoaders).then(() => {
        this.loadStaff()
      })
    },
    loadTimeseries(datasetName, itemName) {
      this.timeseries[datasetName] = {}

      const query = {
        series_name: datasetName,
        item_name: itemName,
      }
      return this.$BwfApi.get("timeseries", query).then(response => {
        this.timeseries[datasetName][itemName] = response
      })
    },
    chartOptions(dataset) {
      const data = this.timeseries[dataset]

      const series = []
      for (const seriesIdentifier in data) {
        const values = []
        for (const row of data[seriesIdentifier]) {
          const d = new Date(row.record_time)
          values.push([d, Math.round(10 * row.value) / 10])
        }

        const seriesName = this.staff.find(item => item.bwf_userid === seriesIdentifier).name

        // hide emtpy series
        if (data[seriesIdentifier].length > 0) {
          series.push({
            name: seriesName,
            type: "line",
            smooth: true,
            data: values,
          })
        }
      }

      const min = new Date()
      min.setMonth(min.getMonth() - 12)
      const max = new Date()

      return {
        xAxis: {
          type: "time",
          min: min,
          max: max,
        },
        yAxis: {
          type: "value",
          name: "Hours",
        },
        legend: {
          top: "30"
        },
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow"
          }
        },
        series: series
      }
    },
  },
  mounted() {
    this.loadAll()
  },
  data() {
    return {
      loaded: false,
      staff: {},
      workload: [], // all workload records as an array
      timeseries: {},
      selectedStaff: null,
      jobs: [],
    }
  }
}
</script>


<template>
  <div v-if="loaded ">
    <h3>Staff Workload</h3>
    <v-simple-table>
      <thead>
      <tr>
        <th class="text-left">Staff Member</th>
        <th class="text-left">Planned Time (hrs)</th>
        <th></th>
      </tr>
      </thead>
      <tbody>

      <tr v-for="staff in workloadSummary" :key="staff.bwf_userid">
        <td>{{ staff.name }}</td>
        <td>{{ staff.currentLoad }}</td>
        <td>
          <v-btn @click="selectedStaff = staff.bwf_userid" dense rounded>
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
        </td>
      </tr>
      </tbody>
    </v-simple-table>

    <h3 class="mt-3">Workload Trends</h3>
    <v-chart class="chart" :option="chartOptions('Workload')" autoresize/>

    <v-dialog v-model="selectedStaff" width="500">
      <v-card>
        <v-card-title class="text-h5 grey lighten-2">
          {{ staff.find(item => item.bwf_userid === selectedStaff)?.name || "Workload Details" }}
        </v-card-title>

        <v-card-text>
          <v-simple-table>
            <thead>
            <tr>
              <th class="text-left">ID</th>
              <th class="text-left">Name</th>
              <th class="text-left">Time (hrs)</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="job in workloadDetails" :key="job.job_id">
              <td><router-link :to="`/jobs/review-pm?JobId=${ job.job_id}`" target="_blank">{{ job.job_id }}</router-link></td>
              <td>{{ getJobName(job.job_id) }}</td>
              <td>{{ job.hours }}</td>
            </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>


<style scoped>
.chart {
  height: 600px;
}

</style>